import logos from '@/content/logos.json'

/**
 * One customer logo in the wall.
 *
 * Separate from `SitePhoto` on purpose, because almost nothing is shared: a logo
 * is line art with transparency, sized by height rather than width, never lazy
 * (the wall is one screen down and loads as a set — a logo appearing after its
 * neighbours reads as a broken image), and its `alt` is the company name rather
 * than a description.
 *
 * A name with no file renders as text. That is deliberate and it is the reason
 * this component exists rather than a bare `<img>`: the customers page lists
 * companies we do not have a mark for, and a company named in plain type is a
 * weaker claim than a logo but a stronger one than a gap where a logo failed.
 */
type Manifest = Record<string, { width: number; height: number; webp: { w: number; src: string }[]; fallback: string }>

/**
 * Where the mark is being drawn.
 *
 * `wall` is the default and the original: one of 55 in a row, capped to a shared
 * height so no brand shouts. `plate` is the customer story card — one mark alone
 * in a white tile several times that size, where the wall's cap leaves a stamp in
 * the middle of an empty rectangle, and where a mark that arrived on a padded
 * canvas (see `content/customerLogos.ts`) shrinks to nothing. In a plate the mark
 * takes the tile instead, which is the same equal-ink argument applied to a tile
 * rather than a row: a wide wordmark runs out of width first, a square mark runs
 * out of height first, and both end up filling it.
 *
 * **The plate sizes the mark against the tile, in percentages, never against the
 * file.** `max-h-full max-w-full` did the second thing, and the twenty-two cards
 * were the result: it only ever *caps*, so what a mark actually rendered at was
 * decided by the pixel size of whatever file we were sent, and the two batches in
 * `content/customerLogos.ts` are two different kinds of file. Asked for on
 * 2026-08-27 with a screenshot of the grid: *"her biri farklı tasarımda
 * olmasın"*.
 *
 * `quote` is the Hall of Fame card, added 2026-08-27 on *"hall of fame deki
 * şirket logolarının büyüklüğünü eşit yapar mısın"*. It is neither of the other
 * two, because a quote card holds **one mark alone above a paragraph** with no
 * tile around it and no neighbour to be equal *to* — the only comparison a
 * reader makes is with the card above and the card below, as they scroll. So
 * every mark is drawn at **one width**, full stop, and its height is whatever
 * the file's aspect gives.
 *
 * That is not the wall's rule and it is not an oversight. The wall caps by
 * height because 55 marks share a row and a six-times-wide wordmark beside a
 * square badge is one brand shouting; nineteen marks stacked a screen apart are
 * never seen together, and there the height cap is what *causes* the
 * inconsistency — eighteen of the nineteen files are wide wordmarks, half of
 * them on a padded 300x300 canvas, so `max-h-14` printed Kordsa's ink about 50px
 * across and Brisa's tight crop at 150. One width makes the ink the same size on
 * every card, which is what was asked for.
 *
 * No height cap, deliberately: a padded square drawn 140px wide is 140px tall
 * and its ink — never more than half the canvas on any of the twenty-two — stays
 * inside the `h-20` slot `pages/Testimonials.tsx` gives it. The overhang is
 * transparent canvas. A `max-h` here would instead letterbox the square and
 * shrink the ink back to the size this change exists to fix.
 */
/**
 * `card` is the pared-back quote card drawn two across, added 2026-08-29 on
 * *"iki kutucuk da bütünlüklü ve birbiri ile uyumlu olsun, logolar…"*. It is the
 * one place `quote`'s reasoning does not hold: those two marks sit **side by
 * side in one row**, so a reader compares them directly the way they do on the
 * wall. One width there draws a square badge as a 140px-tall canvas overflowing
 * a 64px slot — the ink ends up looking half the size of the wordmark beside it,
 * which is exactly the mismatch that was pointed at. So the shared measure here
 * is the slot's height, with a width ceiling so a wide tight crop letterboxes
 * into the slot rather than running across the card. The ceiling tracks the
 * slot: it doubled to 340px when the two-across slot doubled to `h-48` on
 * 2026-08-29, or a wide mark would have stopped at the old 170 while the square
 * ones beside it kept growing.
 *
 * That is the rule for a `card` mark with no `ink` on it. One that declares its
 * ink is cropped to the mark instead and sized by width — the slot's height
 * stops being the measure, because the thing the slot was measuring was mostly
 * transparent canvas.
 *
 * A mark that declares `inkWidth` as well is cropped on both axes and sized by
 * `CARD_INK`. Cropping the height alone left the shorter, narrower mark
 * horizontally centred in a full-width box, so it began an inch inside its
 * card while the wordmark beside it began on the padding — the pair looked
 * like two designs rather than one, which is what a screenshot of the two-up
 * asked about on 2026-09-01.
 */
type Fit = 'wall' | 'plate' | 'quote' | 'card'

/**
 * How wide a `card` mark is drawn when it declares its `ink`. The mark is
 * cropped to that fraction of the canvas, so the width is the shared measure
 * again and the height is whatever the mark itself needs — a wordmark ends up a
 * slim strip, a badge a block, and neither carries a band of empty canvas.
 */
const CARD_WIDTH = 210

/**
 * The shared measure for two `card` marks that both declare their ink box —
 * the geometric mean of the drawn mark's width and height, in pixels.
 *
 * Neither equal width nor equal height works for a pair like LC Waikiki's
 * six-to-one wordmark and Litehouse's near-square badge: matched on width the
 * badge towers over the card, matched on height the wordmark shrinks to a line
 * of small print. `sqrt(w × h)` is the compromise a designer makes by eye — it
 * holds the *presence* of the two marks equal and lets each keep its own shape.
 * 80 is the value that leaves the wordmark at about the size it already had, so
 * this is a levelling of the pair rather than a resize of both.
 */
const CARD_INK = 80

/**
 * The plate's three numbers, as fractions of the square tile, for a mark that
 * declares how much of its canvas is ink (see `content/customerLogos.ts`).
 *
 * Fitting the *canvas* to the tile made every file the same size and left the
 * marks inside them anything but — a 138x75 badge on a 300x300 canvas drew half
 * the width of the wordmark beside it. So the plate now scales each file until
 * the *mark* hits a shared measure, and the canvas around it is allowed to
 * overflow the tile, which costs nothing because it is transparent.
 *
 * `PLATE_WIDTH` is the shared measure and does most of the work: a row of
 * wordmarks all reach it and stop, so they draw the same width they always did
 * and the grid does not move. `PLATE_PRESENCE` — `sqrt(w x h)`, the same
 * compromise `CARD_INK` makes — is what a compact or tall mark hits first, so
 * Litehouse's badge and Akkök's stacked mark are levelled against the wordmarks
 * by *presence* rather than stretched to their width. `PLATE_HEIGHT` is the last
 * guard, for a mark tall enough to reach the tile's edge before either.
 *
 * Chosen so the marks that were already right stay where they are: the value
 * that leaves Akkök — *"akkök ün logosu uygun"*, 2026-09-04 — within a few
 * pixels of what it drew before is the one the other twenty-two are levelled to.
 */
const PLATE_PRESENCE = 0.47
const PLATE_WIDTH = 0.8
const PLATE_HEIGHT = 0.62

export default function SiteLogo({
  logo,
  fit = 'wall',
}: {
  logo: {
    name: string
    src: string
    padded?: boolean
    ink?: number
    inkWidth?: number
    inkScale?: number
    inkOffsetY?: number
  }
  fit?: Fit
}) {
  const entry = (logos as Manifest)[logo.src]

  if (!entry) {
    if (import.meta.env.DEV) console.warn(`no logo in the manifest for ${logo.src}`)
    return <span className="text-sm font-semibold text-muted-foreground">{logo.name}</span>
  }

  // Equal height is not equal weight. A wordmark like SIEMENS is six times as
  // wide as it is tall, so capping every mark at one height renders it 150px
  // across while Unilever's near-square U renders 40px across — and the reader
  // sees one brand shouting and one whispering. So the cap follows the shape:
  // wide marks are limited by height, square ones get a taller allowance, and
  // both end up occupying a similar amount of ink.
  //
  // **Width is the shared measure, height is the ceiling.** Equal *area* is not
  // what a reader compares. Sizing purely for equal ink area let a wide wordmark
  // run to 150px across while a 3:2 crop beside it stopped at 84 — arithmetically
  // the same amount of ink, and on the page one mark plainly bigger than its
  // neighbour, which is what a screenshot of this wall was sent to point at on
  // 2026-09-01: *"bazıları büyük bazıları küçük ... olmasın"*. Eyes read width
  // across a row, not area.
  //
  // So every mark is drawn to the same 130px width unless its height cap stops
  // it first, and the cap only exists so a square badge does not tower over the
  // wordmarks: below 2.5 it is one height for every shape, so the 150x100
  // marketing crops (the bulk of the wall) all draw 96x64, Hopi's near-2:1 draws
  // 126x64, and Siemens draws 130x21. The spread of drawn widths is 58–130
  // rather than 67–150.
  const aspect = entry.width / entry.height
  const cap =
    aspect >= 5
      ? 'max-h-8'
      : aspect >= 3.5
        ? 'max-h-10'
        : aspect >= 2.5
          ? 'max-h-12'
          : 'max-h-16'

  // A plate gives every mark the same box, in the same place, and lets
  // `object-contain` scale the file into it — so a 318x50 crop and a 228x300
  // canvas are drawn at the same size on the card whatever their files weigh.
  //
  // The box is not quite identical for all three shapes, and that is the whole
  // point rather than an inconsistency: half of these files carry their own
  // whitespace and half do not. A portrait canvas is a wordmark floating in
  // about a third of empty space top and bottom (see `kordsa`, `yazaki`,
  // `a101`), so it takes the tile whole and its own padding becomes the
  // margin; a tight crop (`petrolofisi`, `brisa`) has no padding of its own and
  // would otherwise run edge to edge, so it is inset to leave the margin the
  // padded ones bring with them. The numbers are chosen so the *ink* — the
  // drawn mark, not the canvas around it — lands at roughly three quarters of
  // the tile's width in all three cases.
  //
  // The middle bucket is where the `*card.png` set lands — ten 300x300 squares
  // with the mark centred and a little air around it (see
  // `content/customerLogos.ts`). Less padding of their own than a portrait
  // canvas, more than a tight crop, so they sit between the two.
  const plate =
    aspect < 0.9 ? 'h-full w-full' : aspect < 1.2 ? 'h-[86%] w-[86%]' : 'h-[74%] w-[74%]'

  // A plate mark that knows its own ink is sized by the ink instead — the
  // canvas is scaled until the drawn mark reaches whichever of the three
  // measures above it meets first. The fractions are of the canvas, so the
  // drawn ink height has to come back through the file's aspect before it can
  // be compared with the tile.
  const plateScale =
    fit === 'plate' && logo.ink && logo.inkWidth
      ? Math.min(
          PLATE_PRESENCE / Math.sqrt((logo.inkWidth * logo.ink) / aspect),
          PLATE_WIDTH / logo.inkWidth,
          (PLATE_HEIGHT * aspect) / logo.ink,
        )
      : null

  // A padded file on the wall is the one case where capping by height is the
  // wrong answer, and it is the `albarakaturk.png` problem recorded in
  // `content/customerWall.ts`: the mark occupies about a seventh of a square
  // canvas's height, so `max-h-14` draws a wordmark roughly 45px across next to
  // Turkish Airlines at 150. Sizing by width instead makes the *ink* the shared
  // measure again — 120px of canvas puts about 100px of drawn mark in the row,
  // which is what the same brand's tight crop rendered at. The extra canvas
  // overflows the `h-14` row into its 40px gutter and is transparent, so it
  // costs the layout nothing.
  // One width for every mark in a quote card — see the `quote` note on `Fit`.
  // `max-w-none` because the wall's 150px ceiling would otherwise clip it, and
  // `h-auto` so the aspect decides the height rather than any inherited row.
  // A card mark that says how much of its canvas is ink gets that canvas
  // cropped away: the file is drawn `CARD_WIDTH` across and the box is only as
  // tall as the mark, with `object-cover` clipping the transparent remainder
  // top and bottom. Asked for on 2026-08-30 — *"logoların etrafında kocaman
  // boşluklar var"* — and it is the file's padding, not the layout's: both
  // marks on the home page's two-up are 300x300 canvases, so an `h-48` slot
  // drew LC Waikiki's wordmark 24px tall in the middle of 190px of nothing.
  // Cropping beats shrinking the slot, which would have shrunk the ink too.
  // The mark is centred in these canvases, which is why `object-cover`'s own
  // centring is enough and no per-file offset is needed.
  // A mark that declares BOTH fractions is cropped on both axes, which is the
  // only way two marks in one row can start on the same left edge. The file is
  // scaled so its ink lands at `CARD_INK`, then a box exactly the size of the
  // ink is placed over it and the transparent remainder is clipped away on all
  // four sides — the marks in these canvases are centred, so centring the box
  // is enough and no per-file offset is needed. `object-cover` cannot do this:
  // it derives its own scale from the box, so asking for a short box only ever
  // makes the mark smaller, never trims the air beside it.
  const inkBox =
    fit === 'card' && logo.ink && logo.inkWidth
      ? (() => {
          // `inkScale` is the escape hatch on the shared measure, for the one
          // thing `sqrt(w × h)` gets wrong: a compact badge and a long wordmark
          // can hold the same ink area and still not look the same size,
          // because a badge spends its area on two axes and a wordmark on one.
          // Scaling one mark keeps the pair on the shared bottom line — the
          // slot is `items-end`, so a taller mark grows upward and both still
          // stand on the same edge with their quotes starting on the same line.
          const measure = CARD_INK * (logo.inkScale ?? 1)
          const ratio = Math.sqrt((logo.inkWidth! * entry.width) / (logo.ink! * entry.height))
          const width = Math.round(measure * ratio)
          const height = Math.round(measure / ratio)
          // A few pixels of bleed around the clip box, because the fractions
          // are measured off the file by eye and the mark is only *roughly*
          // centred in its canvas. Too generous a box costs a hairline of
          // transparent air nobody can see; too tight a one shaves the descender
          // off a wordmark, which everybody can.
          const bleed = 4
          return {
            width: width + bleed * 2,
            height: height + bleed * 2,
            imgWidth: Math.round(width / logo.inkWidth!),
            imgHeight: Math.round(height / logo.ink!),
            // Fraction of canvas height the ink's true centre sits below (+) or
            // above (-) the canvas centre. The crop assumes centred ink by
            // default; `lcwaikikicard.png`'s wordmark actually sits noticeably
            // below its canvas's middle, so the centred assumption clipped it
            // toward the bottom of its box and it read as sitting lower than
            // Litehouse's badge even after `items-end` bottom-aligned the boxes
            // themselves (2026-09-02, "waikiki daha altta gibi duruyor").
            offsetY: logo.inkOffsetY ?? 0,
          }
        })()
      : null

  const cropped = !inkBox && fit === 'card' && logo.ink ? logo.ink : null
  const crop = cropped
    ? { width: CARD_WIDTH, height: Math.round((CARD_WIDTH * entry.height * cropped) / entry.width) }
    : undefined

  const sizing = inkBox || crop
    ? ''
    : fit === 'plate'
      ? plateScale
        ? 'h-auto'
        : plate
      : fit === 'quote'
        ? 'h-auto w-[140px] max-w-none'
        : fit === 'card'
          ? 'h-full w-auto max-w-[340px]'
          : logo.padded
            ? 'w-[120px] max-w-none'
            : `${cap} w-auto max-w-[130px]`

  if (inkBox) {
    return (
      <span
        className="block overflow-hidden"
        style={{ width: inkBox.width, height: inkBox.height }}
      >
        <img
          src={entry.fallback}
          srcSet={entry.webp.map((v) => `${v.src} ${v.w}w`).join(', ')}
          sizes={`${inkBox.imgWidth}px`}
          width={inkBox.imgWidth}
          height={inkBox.imgHeight}
          style={{
            width: inkBox.imgWidth,
            height: inkBox.imgHeight,
            marginLeft: -Math.round((inkBox.imgWidth - inkBox.width) / 2),
            marginTop: -Math.round(
              (inkBox.imgHeight - inkBox.height) / 2 + inkBox.offsetY * inkBox.imgHeight,
            ),
          }}
          alt={logo.name}
          loading="lazy"
          decoding="async"
          className="max-w-none object-contain"
        />
      </span>
    )
  }

  return (
    <img
      src={entry.fallback}
      srcSet={entry.webp.map((v) => `${v.src} ${v.w}w`).join(', ')}
      // The wall draws to a 130px width, so that is what it asks for. A plate is
      // wider than that, so it asks for the 300 the manifest already builds
      // rather than upscaling. A quote card draws 140 and takes the same 150 as
      // the wall used to (the 300 on a retina screen).
      sizes={
        fit === 'plate'
          ? `${Math.round(260 * (plateScale ?? 1))}px`
          : fit === 'quote'
            ? '140px'
            : fit === 'card'
              ? '340px'
              : '130px'
      }
      width={crop ? crop.width : entry.width}
      height={crop ? crop.height : entry.height}
      style={
        crop
          ? { width: crop.width, height: crop.height }
          : plateScale
            ? { width: `${(plateScale * 100).toFixed(1)}%`, maxWidth: 'none' }
            : undefined
      }
      alt={logo.name}
      loading="lazy"
      decoding="async"
      className={`${sizing} ${crop ? 'object-cover' : 'object-contain'}`}
    />
  )
}
